import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { Card, Row, Col, Table, Spinner } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { toast } from 'react-toastify';

import * as ChartJS from 'chart.js';

import {
  baoCaoGetChiTietPhatTrienPhanMemAction,
  baoCaoClearChiTietPhatTrienPhanMem,
} from '@app/store/reducers/baocao';

import { teamGetAllAction } from '@app/store/reducers/team';
import { objectShowNumber, rangeDateReport, messageToast } from '@app/utils/define';
import { URI_INTERNAL } from '@app/utils/pathLocations';

const pick = (obj, keys, fallback) => {
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return fallback;
};

const mapItem = (x) => ({
  projectId: pick(x, ['projectId', 'ProjectId'], 0),
  tenPhanMem: pick(x, ['tenPhanMem', 'TenPhanMem'], ''),
  nhomThucHien: pick(x, ['nhomThucHien', 'NhomThucHien'], ''),
});

const slicePage = (arr, page, pageSize) => {
  const list = Array.isArray(arr) ? arr : [];
  const p = Number(page) || 1;
  const s = Number(pageSize) || 5;
  const start = (p - 1) * s;
  return list.slice(start, start + s);
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const RANGE_CUSTOM = 'Custom Range';

const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };

  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };

  return {
    from: toYMD(pair[0]),
    to: toYMD(pair[1]),
  };
};

const normalizeFromTo = (from, to) => {
  const f = String(from || '').trim();
  const t = String(to || '').trim();
  if (f && t && t < f) return { from: t, to: f };
  return { from: f, to: t };
};

const findPresetLabelByFromTo = (from, to) => {
  const f = String(from || '').trim();
  const t = String(to || '').trim();
  if (!f && !t) return '';

  for (let i = 0; i < RANGE_OPTIONS.length; i++) {
    const label = RANGE_OPTIONS[i];
    if (label === RANGE_CUSTOM) continue;

    const { from: pf, to: pt } = getRangeFromTo(label);
    if (pf === f && pt === t) return label;
  }
  return RANGE_CUSTOM;
};

const ChartCtor = ChartJS?.Chart || ChartJS?.default || ChartJS;

if (ChartCtor && typeof ChartCtor.register === 'function' && Array.isArray(ChartJS.registerables)) {
  ChartCtor.register(...ChartJS.registerables);
}

const parseTeamsFromText = (text) => {
  const raw = String(text || '').trim();
  if (!raw) return [];
  return raw
    .split(',')
    .map((x) => String(x || '').trim())
    .filter(Boolean);
};

const buildTeamAgg = ({ dangThucHien = [], daHoanThanh = [] }) => {
  const map = new Map();

  const add = (teamName, key, inc = 1) => {
    const name = teamName || 'Chưa gán nhóm';
    const cur = map.get(name) || { doing: 0, done: 0 };
    cur[key] += inc;
    map.set(name, cur);
  };

  (Array.isArray(dangThucHien) ? dangThucHien : []).forEach((it) => {
    const teams = parseTeamsFromText(it?.nhomThucHien);
    if (!teams.length) {
      add('Chưa gán nhóm', 'doing', 1);
      return;
    }
    teams.forEach((t) => add(t, 'doing', 1));
  });

  (Array.isArray(daHoanThanh) ? daHoanThanh : []).forEach((it) => {
    const teams = parseTeamsFromText(it?.nhomThucHien);
    if (!teams.length) {
      add('Chưa gán nhóm', 'done', 1);
      return;
    }
    teams.forEach((t) => add(t, 'done', 1));
  });

  const rows = Array.from(map.entries()).map(([team, v]) => ({
    team,
    doing: toNum(v?.doing),
    done: toNum(v?.done),
    total: toNum(v?.doing) + toNum(v?.done),
  }));

  rows.sort((a, b) => toNum(b.total) - toNum(a.total));
  return rows;
};

/* Chart 1: Team stacked bar */
const TeamStackedBarChart = ({ dangThucHien = [], daHoanThanh = [], loading = false }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const chartPayload = useMemo(() => {
    const rows = buildTeamAgg({ dangThucHien, daHoanThanh });

    const labels = rows.map((r) => r.team);
    const dataDoing = rows.map((r) => r.doing);
    const dataDone = rows.map((r) => r.done);

    return {
      labels,
      datasets: [
        {
          label: 'Đang thực hiện',
          data: dataDoing,
          backgroundColor: '#2563eb',
          borderColor: '#2563eb',
          borderWidth: 1,
        },
        {
          label: 'Hoàn thành',
          data: dataDone,
          backgroundColor: '#10b981',
          borderColor: '#10b981',
          borderWidth: 1,
        },
      ],
    };
  }, [dangThucHien, daHoanThanh]);

  useEffect(() => {
    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch {}
      chartRef.current = null;
    }

    if (loading) return;
    if (!canvasRef.current) return;
    if (!chartPayload?.labels?.length) return;
    if (!ChartCtor) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const isV3Plus = !!(ChartCtor && typeof ChartCtor.register === 'function');

    const optionsV3 = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            footer: (tooltipItems) => {
              const total = (tooltipItems || []).reduce((sum, it) => sum + toNum(it?.parsed?.y), 0);
              return `Tổng: ${total}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { autoSkip: false, maxRotation: 0, minRotation: 0 },
        },
        y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
      },
    };

    const optionsV2 = {
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          footer: (tooltipItems) => {
            const total = (tooltipItems || []).reduce((sum, it) => sum + toNum(it?.yLabel), 0);
            return `Tổng: ${total}`;
          },
        },
      },
      legend: { position: 'bottom' },
      scales: {
        xAxes: [
          {
            stacked: true,
            gridLines: { display: false },
            ticks: { autoSkip: false },
          },
        ],
        yAxes: [
          {
            stacked: true,
            ticks: { beginAtZero: true, precision: 0 },
          },
        ],
      },
    };

    chartRef.current = new ChartCtor(ctx, {
      type: 'bar',
      data: chartPayload,
      options: isV3Plus ? optionsV3 : optionsV2,
    });

    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch {}
        chartRef.current = null;
      }
    };
  }, [chartPayload, loading]);

  return (
    <div className="card h-100">
      <div className="card-header">
        <div className="d-flex align-items-center justify-content-between">
          <div className="card-title mb-0" style={{ fontWeight: 700 }}>
            Khối lượng theo nhóm (Đang thực hiện / Hoàn thành)
          </div>
        </div>
      </div>

      <div className="card-body">
        {loading ? (
          <div>Đang tải...</div>
        ) : !chartPayload?.labels?.length ? (
          <div className="text-muted">Không có dữ liệu để hiển thị biểu đồ.</div>
        ) : (
          <div style={{ height: 340 }}>
            <canvas ref={canvasRef} />
          </div>
        )}
      </div>
    </div>
  );
};

/* Chart 2: Status donut */
const StatusDonutChart = ({ doing = 0, done = 0, loading = false }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const chartPayload = useMemo(() => {
    const d = toNum(doing);
    const h = toNum(done);
    return {
      labels: ['Đang thực hiện', 'Hoàn thành'],
      datasets: [
        {
          data: [d, h],
          backgroundColor: ['#2563eb', '#10b981'],
          borderColor: ['#2563eb', '#10b981'],
          borderWidth: 1,
        },
      ],
    };
  }, [doing, done]);

  useEffect(() => {
    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch {}
      chartRef.current = null;
    }

    if (loading) return;
    if (!canvasRef.current) return;
    if (!ChartCtor) return;

    const total = toNum(doing) + toNum(done);
    if (!total) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const isV3Plus = !!(ChartCtor && typeof ChartCtor.register === 'function');

    const optionsV3 = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (it) => {
              const value = toNum(it?.parsed);
              const pct = total ? Math.round((value / total) * 100) : 0;
              const label = it?.label || '';
              return `${label}: ${value} (${pct}%)`;
            },
          },
        },
      },
      cutout: '60%',
    };

    const optionsV2 = {
      responsive: true,
      maintainAspectRatio: false,
      legend: { position: 'bottom' },
      tooltips: {
        callbacks: {
          label: (tooltipItem, data) => {
            const idx = tooltipItem.index;
            const label = (data.labels && data.labels[idx]) || '';
            const value = toNum(data?.datasets?.[0]?.data?.[idx]);
            const pct = total ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} (${pct}%)`;
          },
        },
      },
      cutoutPercentage: 60,
    };

    chartRef.current = new ChartCtor(ctx, {
      type: 'doughnut',
      data: chartPayload,
      options: isV3Plus ? optionsV3 : optionsV2,
    });

    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch {}
        chartRef.current = null;
      }
    };
  }, [chartPayload, loading, doing, done]);

  const total = toNum(doing) + toNum(done);

  return (
    <div className="card h-100">
      <div className="card-header">
        <div className="card-title mb-0" style={{ fontWeight: 700 }}>
          Cơ cấu trạng thái
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <div>Đang tải...</div>
        ) : !total ? (
          <div className="text-muted">Không có dữ liệu để hiển thị biểu đồ.</div>
        ) : (
          <div style={{ height: 340 }}>
            <canvas ref={canvasRef} />
          </div>
        )}
      </div>
    </div>
  );
};

const ReportTable = ({ title, rows, totalCount, page, pageSize, onChangePage }) => {
  const list = Array.isArray(rows) ? rows : [];
  const paged = useMemo(() => slicePage(list, page, pageSize), [list, page, pageSize]);
  const startIndex = (page - 1) * pageSize;

  const returnUrl = encodeURIComponent(
    `${window.location.pathname}${window.location.search || ''}`
  );

  return (
    <Card className="h-100">
      <Card.Header>
        <div className="d-flex align-items-center">
          <div style={{ flex: 1, fontWeight: 700 }}>{title}</div>

          <div style={{ flex: 1, textAlign: 'center', fontWeight: 700 }}>
            Tổng: <span style={{ color: 'red', fontWeight: 700 }}>{totalCount}</span>
          </div>

          <div style={{ flex: 1 }} />
        </div>
      </Card.Header>

      <Card.Body className="p-0">
        <Table bordered hover className="mb-0 text-center align-middle" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '70px' }} />
            <col style={{ width: 'auto' }} />
            <col style={{ width: '220px' }} />
            <col style={{ width: '110px' }} />
          </colgroup>

          <thead>
            <tr>
              <th>STT</th>
              <th>Phần mềm</th>
              <th>Nhóm thực hiện</th>
              <th>Chức năng</th>
            </tr>
          </thead>

          <tbody>
            {paged.length > 0 ? (
              <>
                {paged.map((r, idx) => (
                  <tr key={`${r.projectId}-${startIndex + idx}`}>
                    <td>{startIndex + idx + 1}</td>

                    <td className="text-center">
                      <span className="d-block w-100 text-truncate" title={r.tenPhanMem}>
                        {r.tenPhanMem}
                      </span>
                    </td>

                    <td>
                      <span className="d-block w-100 text-truncate" title={r.nhomThucHien}>
                        {r.nhomThucHien}
                      </span>
                    </td>

                    <td>
                      <ul className="nav justify-content-center">
                        <li className="nav-item">
                          <NavLink
                            to={`${URI_INTERNAL.PROJECT_DETAIL_PATH}?id=${r.projectId}&returnUrl=${returnUrl}`}
                            exact
                            className="nav-link"
                            title="Xem chi tiết"
                          >
                            <i className="nav-icon font-icon fas fa-eye" />
                          </NavLink>
                        </li>
                      </ul>
                    </td>
                  </tr>
                ))}
              </>
            ) : (
              <tr>
                <td colSpan="4">Không có dữ liệu</td>
              </tr>
            )}
          </tbody>
        </Table>

        {totalCount > pageSize ? (
          <div className="dataTables_paginate paging_simple_numbers p-2">
            <Pagination
              itemClass="page-item"
              linkClass="page-link"
              innerClass="pagination pagination-sm no-margin pull-right"
              activePage={page}
              itemsCountPerPage={Number(pageSize)}
              totalItemsCount={Number(totalCount)}
              pageRangeDisplayed={5}
              onChange={onChangePage}
            />
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
};

const BaoCaoChiTietPhatTrienPhanMem = ({
  teamId = null,
  tenTeam = '',
  From = '',
  To = '',
  commonFrom = '',
  commonTo = '',
  commonRangeLabel = '',
  commonVersion = 0, 
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    chiTietPhatTrienPhanMem,
    loadingChiTietPhatTrienPhanMem,
    errorChiTietPhatTrienPhanMem,
  } = useSelector((s) => s.baocao || {});

  const teamState = useSelector((s) => s.team || {});
  const teams = Array.isArray(teamState.list) ? teamState.list : [];

  const [pageSize, setPageSize] = useState(5);
  const [selectedTeamId, setSelectedTeamId] = useState(
    teamId === null || teamId === undefined ? '' : String(teamId)
  );

  const [pageDangThucHien, setPageDangThucHien] = useState(1);
  const [pageDaHoanThanh, setPageDaHoanThanh] = useState(1);

  const initCommonLabel = useMemo(() => String(commonRangeLabel || '').trim(), [commonRangeLabel]);
  const initCommon = useMemo(() => {
    const norm = normalizeFromTo(commonFrom, commonTo);
    return { from: norm.from, to: norm.to };
  }, [commonFrom, commonTo]);

  const [rangeLabel, setRangeLabel] = useState(initCommonLabel);
  const [fromDate, setFromDate] = useState(initCommon.from);
  const [toDate, setToDate] = useState(initCommon.to);

  const [appliedFrom, setAppliedFrom] = useState(initCommon.from);
  const [appliedTo, setAppliedTo] = useState(initCommon.to);

  const effectiveFrom = useMemo(() => String(appliedFrom || '').trim(), [appliedFrom]);
  const effectiveTo = useMemo(() => String(appliedTo || '').trim(), [appliedTo]);

  const lastCommonSigRef = useRef('');
  const applyCommonToModule = (nextLabel, nextFrom, nextTo) => {
    const label = String(nextLabel || '').trim();
    const norm = normalizeFromTo(nextFrom, nextTo);

    setRangeLabel(label);
    setFromDate(norm.from);
    setToDate(norm.to);
    setAppliedFrom(norm.from);
    setAppliedTo(norm.to);
  };

  useEffect(() => {
    const sig = `${commonVersion}|${String(commonRangeLabel || '')}|${String(commonFrom || '')}|${String(
      commonTo || ''
    )}`;
    if (sig === lastCommonSigRef.current) return;
    lastCommonSigRef.current = sig;

    applyCommonToModule(commonRangeLabel, commonFrom, commonTo);
  }, [commonVersion, commonRangeLabel, commonFrom, commonTo]);

  useEffect(() => {
    dispatch(teamGetAllAction());
  }, [dispatch]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search || '');

    const qTeamId = sp.get('teamId');
    const qFrom = sp.get('From') || '';
    const qTo = sp.get('To') || '';

    if (qTeamId !== null && qTeamId !== undefined && qTeamId !== '') {
      setSelectedTeamId(String(qTeamId));
    } else {
      setSelectedTeamId(teamId === null || teamId === undefined ? '' : String(teamId));
    }

    const initFromQ = String(qFrom || From || '').trim();
    const initToQ = String(qTo || To || '').trim();

    if (initFromQ || initToQ) {
      const norm = normalizeFromTo(initFromQ, initToQ);
      const preset = findPresetLabelByFromTo(norm.from, norm.to);

      setRangeLabel(preset);
      setFromDate(norm.from);
      setToDate(norm.to);
      setAppliedFrom(norm.from);
      setAppliedTo(norm.to);
    }
  }, [location.search, teamId, From, To]);

  useEffect(() => {
    setPageDangThucHien(1);
    setPageDaHoanThanh(1);
  }, [selectedTeamId, pageSize, effectiveFrom, effectiveTo]);

  const effectiveTeamId = useMemo(() => {
    if (selectedTeamId === '' || selectedTeamId === null || selectedTeamId === undefined) return null;
    const n = Number(selectedTeamId);
    return Number.isFinite(n) ? n : null;
  }, [selectedTeamId]);

  useEffect(() => {
    return () => {
      dispatch(baoCaoClearChiTietPhatTrienPhanMem());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      baoCaoGetChiTietPhatTrienPhanMemAction({
        teamId: effectiveTeamId,
        From: effectiveFrom,
        To: effectiveTo,
      })
    );
  }, [dispatch, effectiveTeamId, effectiveFrom, effectiveTo]);

  useEffect(() => {
    if (!errorChiTietPhatTrienPhanMem) return;

    let msg =
      (errorChiTietPhatTrienPhanMem && errorChiTietPhatTrienPhanMem.message) ||
      (typeof errorChiTietPhatTrienPhanMem === 'string' ? errorChiTietPhatTrienPhanMem : '') ||
      (errorChiTietPhatTrienPhanMem && errorChiTietPhatTrienPhanMem.title) ||
      '';

    if (!msg && errorChiTietPhatTrienPhanMem && errorChiTietPhatTrienPhanMem.errors) {
      msg = Object.values(errorChiTietPhatTrienPhanMem.errors).flat().join('; ');
    }

    toast.error(msg || 'Không thể tải báo cáo chi tiết Phát triển phần mềm.');
  }, [errorChiTietPhatTrienPhanMem]);

  const dangThucHien = useMemo(() => {
    const raw = pick(chiTietPhatTrienPhanMem, ['dangThucHien', 'DangThucHien'], []);
    return Array.isArray(raw) ? raw.map(mapItem) : [];
  }, [chiTietPhatTrienPhanMem]);

  const daHoanThanh = useMemo(() => {
    const raw = pick(chiTietPhatTrienPhanMem, ['daHoanThanh', 'DaHoanThanh'], []);
    return Array.isArray(raw) ? raw.map(mapItem) : [];
  }, [chiTietPhatTrienPhanMem]);

  const tongDangThucHien = toNum(
    pick(chiTietPhatTrienPhanMem, ['tongDangThucHien', 'TongDangThucHien'], dangThucHien.length)
  );
  const tongDaHoanThanh = toNum(
    pick(chiTietPhatTrienPhanMem, ['tongDaHoanThanh', 'TongDaHoanThanh'], daHoanThanh.length)
  );

  const headerTeamText = useMemo(() => {
    if (effectiveTeamId === null) return 'Tất cả nhóm';
    if (effectiveTeamId === 0) return 'Chưa gán nhóm';

    const found = teams.find((t) => Number(t.id) === Number(effectiveTeamId));
    if (found?.name) return found.name;

    if (tenTeam) return tenTeam;
    return `TeamId: ${effectiveTeamId}`;
  }, [effectiveTeamId, teams, tenTeam]);

  const onChangeShowNumber = (e) => {
    const newSize = parseInt(e.target.value, 10) || 5;
    setPageSize(newSize);
  };

  const handleChangeRange = (e) => {
    const v = e.target.value || '';
    setRangeLabel(v);

    if (v && v !== RANGE_CUSTOM) {
      const { from, to } = getRangeFromTo(v);
      const norm = normalizeFromTo(from, to);

      setFromDate(norm.from);
      setToDate(norm.to);
      setAppliedFrom(norm.from);
      setAppliedTo(norm.to);
      return;
    }

    if (v === RANGE_CUSTOM) {
      setFromDate(effectiveFrom || '');
      setToDate(effectiveTo || '');
      return;
    }

    setFromDate('');
    setToDate('');
    setAppliedFrom('');
    setAppliedTo('');
  };

  const handleApplyCustomRange = () => {
    if (rangeLabel === RANGE_CUSTOM && !fromDate && !toDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }
    const norm = normalizeFromTo(fromDate, toDate);
    setFromDate(norm.from);
    setToDate(norm.to);
    setAppliedFrom(norm.from);
    setAppliedTo(norm.to);
  };

  const handleClearFilter = () => {
    setSelectedTeamId('');
    setRangeLabel('');
    setFromDate('');
    setToDate('');
    setAppliedFrom('');
    setAppliedTo('');
  };

  const filterHint =
    effectiveFrom || effectiveTo
      ? `Đang lọc : ${fmtDDMMYYYY(effectiveFrom) || '...'} → ${fmtDDMMYYYY(effectiveTo) || '...'}`
      : '';

  return (
    <div className="container-fluid" style={{ marginTop: 16 }}>
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h3 className="card-title mb-0">Báo cáo Phát triển phần mềm</h3>
                <div className="text-muted" style={{ marginTop: 4 }}>
                  Nhóm: <span style={{ fontWeight: 700 }}>{headerTeamText}</span>
                </div>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
                <form className="form-inline" style={{ minWidth: 220 }}>
                  <label className="mr-2">Hiển thị :</label>
                  <select className="form-control" value={pageSize} onChange={onChangeShowNumber}>
                    {objectShowNumber.map((item, idx) => (
                      <option key={idx} value={item.value}>
                        {item.text}
                      </option>
                    ))}
                  </select>
                </form>

                <div className="d-flex flex-column align-items-start">
                  <div className="d-flex flex-wrap align-items-end gap-2">
                    <div className="form-inline" style={{ minWidth: 260 }}>
                      <label className="mr-2 mb-0">Nhóm thực hiện</label>
                      <select
                        className="form-control"
                        value={selectedTeamId}
                        onChange={(e) => setSelectedTeamId(e.target.value)}
                        disabled={loadingChiTietPhatTrienPhanMem}
                      >
                        <option value="">-- Tất cả nhóm --</option>
                        {teams.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ minWidth: 220 }}>
                      <label className="form-label mb-1">Khoảng thời gian</label>
                      <select
                        className="form-select form-select-sm"
                        value={rangeLabel} 
                        onChange={handleChangeRange}
                        disabled={loadingChiTietPhatTrienPhanMem}
                      >
                        <option value="">-- Chọn --</option>
                        {RANGE_OPTIONS.map((x) => (
                          <option key={x} value={x}>
                            {x}
                          </option>
                        ))}
                      </select>
                    </div>

                    {rangeLabel === RANGE_CUSTOM && (
                      <>
                        <div>
                          <label className="form-label mb-1">Từ ngày</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            disabled={loadingChiTietPhatTrienPhanMem}
                          />
                        </div>

                        <div>
                          <label className="form-label mb-1">Đến ngày</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            disabled={loadingChiTietPhatTrienPhanMem}
                          />
                        </div>

                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={handleApplyCustomRange}
                          disabled={loadingChiTietPhatTrienPhanMem}
                        >
                          Lọc
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleClearFilter}
                      disabled={loadingChiTietPhatTrienPhanMem}
                    >
                      Xóa lọc
                    </button>
                  </div>

                  {filterHint ? (
                    <div className="filter-hint" style={{ marginTop: 6 }}>
                      {filterHint}
                    </div>
                  ) : null}
                </div>
              </div>

              {loadingChiTietPhatTrienPhanMem ? (
                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                  <Spinner animation="border" size="sm" />
                  <span>Đang tải dữ liệu...</span>
                </div>
              ) : (
                <>
                  <Row>
                    <Col lg={6} className="mb-3 mb-lg-0">
                      <ReportTable
                        title="Phần mềm đang thực hiện"
                        rows={dangThucHien}
                        totalCount={tongDangThucHien}
                        page={pageDangThucHien}
                        pageSize={pageSize}
                        onChangePage={(p) => setPageDangThucHien(p)}
                      />
                    </Col>

                    <Col lg={6}>
                      <ReportTable
                        title="Phần mềm đã hoàn thành"
                        rows={daHoanThanh}
                        totalCount={tongDaHoanThanh}
                        page={pageDaHoanThanh}
                        pageSize={pageSize}
                        onChangePage={(p) => setPageDaHoanThanh(p)}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col lg={8} className="mb-3 mb-lg-0">
                      <TeamStackedBarChart
                        dangThucHien={dangThucHien}
                        daHoanThanh={daHoanThanh}
                        loading={loadingChiTietPhatTrienPhanMem}
                      />
                    </Col>

                    <Col lg={4}>
                      <StatusDonutChart
                        doing={tongDangThucHien}
                        done={tongDaHoanThanh}
                        loading={loadingChiTietPhatTrienPhanMem}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default BaoCaoChiTietPhatTrienPhanMem;